"use client";
import Image from "next/image";
import * as images from "@/components/images/Images";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Accordion } from "@/components/ui/accordion";
import Link from "next/link";
import { memo, useState } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import Icon from "@/components/ui/Icon";
import { usePathname } from "next/navigation";
import FooterCTA from "./components/FooterCTA";
import { footerLegalLinks, footerSections, socialLinks } from "./constants";
import FooterAccordionSection from "./components/FooterAccordionSection";

const Footer = () => {
  const [language, setLanguage] = useState("English");
  const isTablet = useMediaQuery("(min-width: 768px)");
  const pathname = usePathname();
  const isCostCalculatorPage = pathname === "/cost-calculator";

  return (
    <footer className="lg:bg-[#1b1f3b] text-white">
      <FooterCTA />
      <div
        className={`section bg-[#1b1f3b] pb-12 md:pb-20 ${
          isCostCalculatorPage ? "" : "lg:mt-[1.375rem]"
        }`}
      >
        <div className="container mx-auto">
          <div
            className={`flex flex-col pt-12 md:pt-20 md:gap-10 ${
              isCostCalculatorPage ? "lg:pt-16" : "lg:pt-0"
            }`}
          >
            <div className="grid grid-cols-1 md:flex md:flex-wrap md:gap-10 md:justify-between md:mb-3">
              <div className="flex flex-col gap-3 md:gap-4.5">
                <div className="flex">
                  <Link href={"/"}>
                    <Image
                      src={images.logoWhite}
                      alt="Languex logo"
                      width={186}
                      height={48}
                    />
                  </Link>
                </div>
                <div className="flex flex-col gap-3 text-base leading-[130%] md:gap-8 md:text-sm md:leading-[19px]">
                  <span>Online Professional Translation</span>
                  <div className="flex items-center md:flex-col md:items-start md:gap-3">
                    <div className="flex gap-4 md:gap-3 items-center hover:underline">
                      {" "}
                      <Icon
                        id="icon-phone_16"
                        width="16"
                        height="17"
                        className="text-[#ddd]"
                      />
                      <Link href="tel:+18335511381">(833) 551-1381</Link>
                    </div>
                    <span className="shrink-0 mx-4 md:hidden">&#10072;</span>
                    <div className="flex gap-3 items-center hover:underline">
                      {" "}
                      <Icon
                        id="icon-mail_16"
                        width="16"
                        height="17"
                        className="text-[#ddd]"
                      />
                      <Link href="mailto:support@languex.co">
                        support@languex.co
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
              <hr className="h-[1px] border-0 bg-white opacity-20 w-full my-6 md:hidden" />
              {/* Footer Menu */}
              <Accordion
                type="multiple"
                className="grid grid-cols-1 gap-2 md:gap-10 md:grid-cols-2 lg:grid-cols-4 lg:gap-[29px] text-[#dddddd] md:w-full md:max-w-[50%] lg:max-w-[674px]"
              >
                {footerSections.map((section) => (
                  <FooterAccordionSection
                    key={section.key}
                    value={section.key}
                    label={section.label}
                    links={section.links}
                    isTablet={isTablet}
                  />
                ))}
              </Accordion>
            </div>
            <hr className="border-0 h-[1px] bg-white opacity-20 w-full my-6 md:my-2" />
            <div className="grid grid-cols-1 md:flex md:flex-wrap md:gap-10 md:justify-between text-[#dddddd] lg:flex lg:justify-between">
              <div className="grid grid-cols-2 gap-y-2 gap-x-4 lg:gap-x-[29px] text-sm leading-[18px] md:leading-[19px] lg:w-full lg:max-w-[405px]">
                {footerLegalLinks.map(({ label, href }) => (
                  <div key={label} className="cursor-pointer hover:underline">
                    {href ? <Link href={href}>{label}</Link> : label}
                  </div>
                ))}
              </div>
              <hr className="h-[1px] border-0 bg-white opacity-20 w-full my-6 md:hidden" />
              <div className="flex items-center justify-center md:justify-start gap-10 flex-wrap">
                <div className="grid grid-cols-1 gap-6 lg:flex lg:items-center">
                  <div className="flex gap-4 md:gap-8 items-center">
                    <Image
                      src={images.footerLogo1}
                      alt="FooterLogo"
                      width={0}
                      height={0}
                    />
                    <Image
                      src={images.footerLogo2}
                      alt="FooterLogo"
                      width={0}
                      height={0}
                    />
                  </div>
                  <div className="flex gap-4 md:gap-8 items-center">
                    <Link
                      href="https://www.trustpilot.com/review/www.languex.co"
                      target="_blank"
                    >
                      <Image
                        src={images.footerLogo4}
                        alt="FooterLogo"
                        width={0}
                        height={50}
                      />
                    </Link>
                    <div className="grid grid-cols-1 gap-[7] text-[13.44] leading-[17px] md:text-sm md:leading-[20px] lg:gap-3 lg:order-5">
                      <span className="md:font-medium">Follow us</span>
                      <div className="flex items-center gap-4">
                        {socialLinks.map(
                          ({ href, iconId, label, width, height, className }) => (
                            <Link
                              key={href}
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center shrink-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded-full"
                              aria-label={label}
                              title={label}
                            >
                              <Icon
                                id={iconId}
                                width={width}
                                height={height}
                                className={className}
                              />
                            </Link>
                          )
                        )}
                      </div>
                    </div>
                    <Image
                      src={images.footerLogo3}
                      alt="FooterLogo"
                      width={0}
                      height={50}
                      quality={100}
                    />
                  </div>
                </div>
              </div>
            </div>
            <hr className="border-0 h-[1px] bg-white opacity-20 w-full my-6 md:my-2" />
            <div className="grid grid-cols-1 gap-2 justify-items-center md:flex md:justify-between md:items-center md:gap-[29] text-[#DDDDDD] md:mt-3">
              <span className="text-sm leading-[130%] text-white text-center md:text-left">
                &copy; {new Date().getFullYear()} Languex · All right reserved ·{" "}
                <br className="md:hidden" />
                6605 Longshore St, Suite 240, Dublin, OH 43017
              </span>
              {/* <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="w-[90px] bg-[#1B1F3B] rounded-3xl cursor-pointer">
                  <SelectValue placeholder="Select Language">
                    <>
                      <Icon
                        id="icon-global_32"
                        width={16}
                        height={16}
                        className="text-white"
                      />
                      {language === "English" ? "En" : "Sp"}
                    </>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-[#1b1f3b] !w-[90px] min-w-0">
                  <SelectItem
                    value="English"
                    className="bg-[#1B1F3B] w-[90px] text-white data-[highlighted]:text-white data-[highlighted]:bg-[#2A2F55] cursor-pointer"
                  >
                    English
                  </SelectItem>
                  <SelectItem
                    value="Spanish"
                    className="bg-[#1B1F3B] w-[90px] text-white data-[highlighted]:text-white data-[highlighted]:bg-[#2A2F55] cursor-pointer"
                  >
                    Spanish
                  </SelectItem>
                </SelectContent>
              </Select> */}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default memo(Footer);
